import { useState, useEffect, useCallback } from 'react';
import { useLang } from '../lib/lang';

/**
 * Premium animated countdown.
 * - mode "hero"    — XXL with glow
 * - mode "card"    — Big visible block on each raffle card
 * - mode "inline"  — small inline mono
 * - soldOut prop   — when true, label shows "Draw scheduled in" instead of "Ends in"
 */
export default function CountdownTimer({ endDate, className = '', large = false, mode = 'inline', soldOut = false }) {
  const { t } = useLang();

  const calcTimeLeft = useCallback(() => {
    const diff = new Date(endDate) - new Date();
    if (diff <= 0) return null;
    return {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((diff % (1000 * 60)) / 1000),
      totalMs: diff,
    };
  }, [endDate]);

  const [timeLeft, setTimeLeft] = useState(calcTimeLeft());

  useEffect(() => {
    const timer = setInterval(() => setTimeLeft(calcTimeLeft()), 1000);
    return () => clearInterval(timer);
  }, [calcTimeLeft]);

  if (!timeLeft) {
    return <span className="text-red-400 font-semibold uppercase tracking-wider text-xs">{t('ended')}</span>;
  }

  // < 24h: red urgent flash
  // < 72h: gold soft pulse
  const isCritical = timeLeft.totalMs < 24 * 3600 * 1000;
  const isUrgent = timeLeft.totalMs < 72 * 3600 * 1000;

  const resolved = mode || (large ? 'card' : 'inline');

  /* ─── CARD MODE: big flashing countdown for raffle cards ─── */
  if (resolved === 'card' || large) {
    const pulseClass = isCritical
      ? 'animate-pulse-red border-red-400/70'
      : isUrgent
        ? 'animate-pulse-gold border-[hsl(var(--gold)/0.6)]'
        : 'border-[hsl(var(--gold)/0.4)]';
    const labelColor = isCritical ? 'text-red-300' : 'text-gold';

    // When sold out, the countdown is for the DRAW (not for ticket sales).
    const labelText = soldOut
      ? (t('drawScheduledIn') || 'Draw scheduled in')
      : (isCritical
          ? (t('endsToday') || 'Ends Today')
          : (t('liveCountdown') || 'Ends In'));

    return (
      <div className={`relative ${className}`}>
        {/* Live label */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5">
            <span className={`w-1.5 h-1.5 rounded-full blink-dot ${isCritical ? 'bg-red-500' : 'bg-gold'}`} />
            <span className={`text-[9px] uppercase tracking-[0.25em] font-bold ${labelColor}`}>
              {labelText}
            </span>
          </div>
        </div>
        <div className={`grid grid-cols-4 gap-1.5 p-2 rounded-xl border-2 bg-black/60 backdrop-blur-sm ${pulseClass}`}>
          <Cell value={timeLeft.days} label={t('days') || 'days'} isCritical={isCritical} />
          <Cell value={timeLeft.hours} label={t('hrs') || 'hrs'} isCritical={isCritical} />
          <Cell value={timeLeft.minutes} label={t('min') || 'min'} isCritical={isCritical} />
          <Cell value={timeLeft.seconds} label={t('sec') || 'sec'} isCritical={isCritical} flash />
        </div>
      </div>
    );
  }

  return (
    <span className={`font-mono font-semibold ${className}`}>
      {timeLeft.days > 0 && <>{timeLeft.days}d </>}
      {String(timeLeft.hours).padStart(2, '0')}:
      {String(timeLeft.minutes).padStart(2, '0')}:
      {String(timeLeft.seconds).padStart(2, '0')}
    </span>
  );
}

function Cell({ value, label, isCritical = false, flash = false }) {
  return (
    <div className="flex flex-col items-center bg-gradient-to-b from-[#08231A] to-[#020805] rounded-lg py-2 sm:py-2.5 px-1 border border-[hsl(var(--gold)/0.25)] shadow-inner min-w-0">
      <span
        className={`font-mono text-xl xs:text-2xl sm:text-3xl md:text-4xl font-extrabold leading-none tabular-nums ${flash ? 'flash-digit' : ''}`}
        style={{
          color: '#FFFFFF',
          textShadow: isCritical
            ? '0 0 14px rgba(239,68,68,0.55), 0 1px 0 rgba(0,0,0,0.5)'
            : '0 0 12px rgba(229,201,122,0.35), 0 1px 0 rgba(0,0,0,0.5)',
          letterSpacing: '0.02em',
        }}
      >
        {String(value).padStart(2, '0')}
      </span>
      <span className="text-[8px] sm:text-[9px] uppercase tracking-[0.18em] sm:tracking-[0.22em] text-gold/90 mt-1 sm:mt-1.5 font-bold">
        {label}
      </span>
    </div>
  );
}
